/* ============================================================
   NICK & JOE — CRÉE TON MIX · Garde-fou panier (V1, phase 2)
   ------------------------------------------------------------
   Rôles :
   1. VERROUILLER l'affichage : masque les steppers de quantité
      et la poubelle de la ligne « Supplément mélange », et le
      stepper des lignes contenant (mix = quantité 1, décision 31).
   2. AUTO-RÉPARER : après chaque changement de panier, recalcule
      la quantité attendue du Supplément à partir des _mix_json
      des lignes contenant. Si le client a modifié/supprimé la
      ligne Supplément (ou supprimé un contenant), on corrige via
      /cart/change.js. Supprimer un mix retire donc ses extras,
      et bidouiller le Supplément le remet au bon montant.
   3. CORRIGER le compteur du panier : n'affiche pas les 100
      unités du Supplément (compte 1 mix = 1 article).

   ⚠️ V1 côté client — le verrou définitif viendra de la future
   app custom (Cart Transform = fusion en une seule ligne).
   ============================================================ */
(function () {
  'use strict';

  const CONFIG = {
    /* Handle du produit Supplément (facturation des extras) — identification
       FIABLE des lignes extras : le produit a désormais PLUSIEURS variantes (une
       par palier de prix), donc on ne peut plus se fier à un seul variant id. */
    SUPPLEMENT_HANDLE: 'supplement-melange',
    /* Ancien id de la variante unique 0,50 $ (prod) : gardé en fallback legacy
       pour reconnaître d'éventuelles lignes d'anciens paniers ; sans objet sur les
       nouvelles variantes par palier (reconnues par handle / propriété « Pour »). */
    EXTRA_VARIANT_ID: 45953649147957,
    /* Line item properties posées par le builder (nj-melanges.js) */
    MIX_ID_PROP: '_mix_grp',      // id contenant ↔ Supplément (renommé depuis `_mix_id`
                                  // pour défaire l'ancienne fonction Cart Transform orpheline, cf. builder)
    MIX_REF_PROP: '_mix_ref',     // même id posé sur les lignes bonbon
    BONBON_PROP: '_mix_bonbon',   // '1' = ligne composant bonbon (0 $, quantity = nb de sacs)
    /* Sélecteurs du badge compteur (Dawn : cart-icon-bubble.liquid → .cart-count-bubble span[aria-hidden]) */
    COUNT_SELECTORS: ['[data-cart-count]', '.cart-count-bubble span[aria-hidden="true"]'],
  };

  let enCorrection = false;
  /* Rôle de chaque ligne, indexé par line item key (rempli depuis /cart.js).
     'mix' = ligne contenant (a _mix_json) · 'supplement' = ligne extras
     'bonbon' = ligne composant bonbon (_mix_bonbon = '1') */
  let rolesParKey = Object.create(null);
  /* key → _mix_grp (contenant + bonbons + supplément) pour regrouper au réordonnancement */
  let mixIdParKey = Object.create(null);

  /* Garde anti-boucle de reload : reconcilier() recharge la page après une
     correction. Si une correction ne « prenait » jamais (cas pathologique),
     on éviterait un rechargement en boucle → hang. On plafonne à 3 reloads par
     fenêtre glissante de 5 s ; au-delà on abandonne (log). Un passage propre
     (aucune correction) remet le compteur à zéro. */
  const RELOAD_KEY = 'njMixReloadGuard';
  function rechargerSecurise() {
    try {
      const now = Date.now();
      let g = JSON.parse(sessionStorage.getItem(RELOAD_KEY) || 'null'); // nj-lint-allow: storage — garde anti-boucle de reload, pas de la donnée métier
      if (!g || now - g.ts > 5000) g = { n: 0, ts: now };
      g.n += 1;
      g.ts = now;
      sessionStorage.setItem(RELOAD_KEY, JSON.stringify(g)); // nj-lint-allow: storage — compteur de reload éphémère
      if (g.n > 3) {
        console.warn('[NJ-MIX] Boucle de reload évitée — réconciliation abandonnée.');
        return;
      }
    } catch (e) { /* sessionStorage indispo : on recharge quand même */ }
    location.reload();
  }
  function resetGardeReload() {
    try { sessionStorage.removeItem(RELOAD_KEY); } catch (e) {} // nj-lint-allow: storage — nettoyage du compteur
  }

  const estBonbon = (item) =>
    item.properties && item.properties[CONFIG.BONBON_PROP] === '1';
  const estSupplement = (item) =>
    !estBonbon(item) && (
      item.variant_id === CONFIG.EXTRA_VARIANT_ID ||
      item.handle === CONFIG.SUPPLEMENT_HANDLE ||
      (item.properties && item.properties['Pour'])
    );
  const estMix = (item) =>
    item.properties && item.properties['_mix_json'] && !estSupplement(item) && !estBonbon(item);

  /* Construit la table key → rôle (+ map mixId) depuis /cart.js */
  function calculerRoles(cart) {
    const map = Object.create(null);
    mixIdParKey = Object.create(null);
    for (const item of cart.items) {
      const props = item.properties || {};
      if (estBonbon(item)) {
        map[item.key] = 'bonbon';
        // Le bonbon référence son contenant via `_mix_ref` (nouveau) ou, pour les
        // lignes d'anciens paniers, via `_mix_id`.
        const ref = props[CONFIG.MIX_REF_PROP] || props[CONFIG.MIX_ID_PROP];
        if (ref) mixIdParKey[item.key] = ref;
      } else if (estSupplement(item)) {
        map[item.key] = 'supplement';
        if (props[CONFIG.MIX_ID_PROP]) mixIdParKey[item.key] = props[CONFIG.MIX_ID_PROP];
      } else if (estMix(item)) {
        map[item.key] = 'mix';
        if (props[CONFIG.MIX_ID_PROP]) mixIdParKey[item.key] = props[CONFIG.MIX_ID_PROP];
      }
    }
    return map;
  }

  /* ─────────── 2. Auto-réparation ─────────── */
  async function reconcilier() {
    if (enCorrection) return;
    try {
      const cart = await (await fetch('/cart.js')).json();

      /* Orphelins : bonbons dont le contenant (même _mix_id) a été supprimé.
         On les retire — sinon ils resteraient au panier et décrémenteraient
         l'inventaire natif sans mélange. */
      const mixIdsAvecContenant = new Set(
        cart.items
          .filter(estMix)
          .map((i) => i.properties && i.properties[CONFIG.MIX_ID_PROP])
          .filter(Boolean)
      );
      const orphelins = cart.items.filter((i) => {
        // Composant d'un mélange = ligne bonbon OU ligne Supplément. Sans la
        // fusion, le Supplément est une ligne séparée : quand on retire le
        // contenant, il faut le retirer aussi (sinon il reste orphelin au panier).
        if (!estBonbon(i) && !estSupplement(i)) return false;
        // `_mix_ref` (bonbons) ou `_mix_id` (Supplément / anciens bonbons) :
        // dès que le contenant lié disparaît, on retire le composant.
        const mid = i.properties && (i.properties[CONFIG.MIX_REF_PROP] || i.properties[CONFIG.MIX_ID_PROP]);
        return mid && !mixIdsAvecContenant.has(mid);
      });
      if (orphelins.length) {
        enCorrection = true;
        for (const o of orphelins) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: o.key, quantity: 0 }),
          });
        }
        enCorrection = false;
        rechargerSecurise();
        return;
      }

      /* Réconcilier CHAQUE ligne Supplément avec les extras de SON mélange, PAR
         PALIER de prix. Depuis les extras-par-produit, un mélange peut avoir
         PLUSIEURS lignes Supplément (une par palier : 0,99 $, 1,99 $…), chacune
         = une variante au prix du palier, quantité = nb de portions à ce palier.
         On apparie chaque ligne à son palier par (mixId, prix unitaire de ligne).
         ⚠️ Bug historique (multi-mélanges) : apparier sans filtrer par mixId
         empilait les extras de tous les mélanges sur une ligne → prix gonflé. */
      const corrections = [];
      for (const contenant of cart.items.filter(estMix)) {
        const mixId = contenant.properties && contenant.properties[CONFIG.MIX_ID_PROP];
        if (!mixId) continue;
        /* La quantité de la ligne contenant = nombre d'exemplaires du mélange
           commandés (stepper actif). Les extras DOIVENT suivre : qté d'un palier
           = (portions à ce palier dans UN mélange) × qtéMix. Le décrément de stock
           multiplie de même par la quantité de ligne (Flow « Run code »). */
        const qtyMix = Math.max(1, contenant.quantity);
        let mix;
        try { mix = JSON.parse(contenant.properties['_mix_json']); } catch (e) { continue; }
        /* Portions de base par palier (cents → portions), depuis _mix_json. Format
           courant : chaque ligne porte `extraUnitCents` (prix/portion) et `qty`
           (portions). Les paniers d'AVANT ce format n'ont qu'un `extraCents` global
           → impossible de ventiler par palier : on saute pour ne pas mettre à 0 à tort. */
        const portionsParPalier = Object.create(null);
        let formatConnu = true;
        for (const l of mix) {
          const unit = l.extraUnitCents;
          if (unit == null) { if ((l.extraCents || 0) > 0) formatConnu = false; continue; }
          if (unit > 0) portionsParPalier[unit] = (portionsParPalier[unit] || 0) + (l.qty || 0);
        }
        if (!formatConnu) continue;
        /* Chaque ligne Supplément de ce mélange = un palier, identifié par son prix
           unitaire. On matche sur `original_price` (prix de la variante AVANT rabais) :
           `price` reflète les rabais panier/automatiques, et un rabais sur la ligne
           Supplément ferait sinon chuter le match à 0 → ligne supprimée à tort.
           On ne RE-crée pas une ligne manquante (le builder la pose au prochain ajout) ;
           on ajuste les quantités existantes — et un palier sans portions tombe à 0. */
        for (const supp of cart.items.filter(
          (i) => estSupplement(i) && i.properties && i.properties[CONFIG.MIX_ID_PROP] === mixId
        )) {
          const palier = supp.original_price != null ? supp.original_price : supp.price;
          const base = portionsParPalier[palier] || 0;
          const attendu = base * qtyMix;
          if (supp.quantity !== attendu) corrections.push({ key: supp.key, quantity: attendu });
        }
      }
      if (corrections.length) {
        enCorrection = true;
        for (const c of corrections) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: c.key, quantity: c.quantity }),
          });
        }
        document.dispatchEvent(new CustomEvent('nj-mix:cart-corrige'));
        enCorrection = false;
        rechargerSecurise();
        return;
      }
      /* Panier cohérent : aucun reload nécessaire → on relâche la garde. */
      resetGardeReload();
      rolesParKey = calculerRoles(cart);
      majCompteur(cart);
      majNoteCommande(cart);
      appliquer();
    } catch (e) { enCorrection = false; }
  }

  /* ─────────── 3. Compteur : 1 mix = 1 article ─────────── */
  /* On retire l'inflation artificielle du item_count : la quantité de la
     ligne Supplément (cents) ET les grammes des lignes bonbon. Reste le
     nombre réel d'articles (1 par contenant/mix + éventuels autres produits). */
  function majCompteur(cart) {
    const quantiteSupp = cart.items
      .filter(estSupplement).reduce((s, i) => s + i.quantity, 0);
    const grammesBonbons = cart.items
      .filter(estBonbon).reduce((s, i) => s + i.quantity, 0);
    const inflation = quantiteSupp + grammesBonbons;
    if (!inflation) return;
    const reel = cart.item_count - inflation;
    /* Cas Dawn : quand item_count >= 100, cart-icon-bubble.liquid N'IMPRIME PAS
       le <span aria-hidden> du nombre → le badge est vide. On (re)crée le span
       pour pouvoir y écrire la valeur corrigée. */
    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      let span = bubble.querySelector('span[aria-hidden="true"]');
      if (!span) {
        span = document.createElement('span');
        span.setAttribute('aria-hidden', 'true');
        bubble.insertBefore(span, bubble.firstChild);
      }
      span.textContent = String(reel);
    });
    /* Autres compteurs éventuels (header sans bulle) : simple remplacement du nombre */
    for (const sel of CONFIG.COUNT_SELECTORS) {
      document.querySelectorAll(sel).forEach((el) => {
        if (/^\d+$/.test(el.textContent.trim())) el.textContent = String(reel);
      });
    }
  }

  /* ─────────── Note de commande lisible (préparation) ─────────── */
  /* Écrit dans la note Shopify de la commande un résumé clair par mélange :
     « <contenant> : <bonbon> × <n>, … » — ce dont la personne qui prépare a
     besoin, sans ouvrir le `</>` des propriétés techniques. Régénérée à chaque
     changement de panier (gère l'ajout ET le retrait de mélanges). */
  async function majNoteCommande(cart) {
    const lignes = [];
    for (const contenant of cart.items.filter(estMix)) {
      const props = contenant.properties || {};
      const bonbons = Object.keys(props)
        .filter((k) => /^Bonbon\s*\d+$/.test(k))
        .sort((a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10))
        .map((k) => String(props[k]).replace(/\s*\(\+[^)]*\)/, '')); // retire le « (+x $) »
      if (bonbons.length) {
        const nom = contenant.product_title || contenant.title || 'Mélange';
        const mult = contenant.quantity > 1 ? ` (× ${contenant.quantity})` : '';
        lignes.push(nom + mult + ' : ' + bonbons.join(', '));
      }
    }
    const desiree = lignes.length ? '🍬 ' + lignes.join('  |  ') : '';
    if ((cart.note || '') !== desiree) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: desiree }),
        });
      } catch (e) { /* silencieux */ }
    }
  }

  /* ─────────── 1. Verrouillage visuel ─────────── */
  /* On repère chaque ligne par son input de quantité Dawn, qui porte
     data-quantity-line-key (= item.key) ET data-quantity-variant-id. Le rôle
     vient de rolesParKey (rempli via /cart.js) ; à défaut on identifie quand
     même le Supplément par son variant id (fiable sans cart.js). Aucune
     dépendance aux handles/hrefs (fragiles) : ça marche sur /cart et le drawer. */
  function verrouillerAffichage() {
    document.querySelectorAll('input[data-quantity-line-key]').forEach((input) => {
      const key = input.getAttribute('data-quantity-line-key');
      let role = rolesParKey[key] || null;
      if (!role) {
        const vid = input.getAttribute('data-quantity-variant-id');
        if (vid && vid === String(CONFIG.EXTRA_VARIANT_ID)) role = 'supplement';
      }
      if (!role) return;
      /* Conteneur de ligne (drawer ou page panier) — Dawn : tr.cart-item */
      const ligne = input.closest('tr.cart-item, .cart-item, tr, li');
      if (!ligne) return;
      /* Stepper de quantité : masqué pour supplément et bonbon (non éditables).
         Le contenant (role 'mix') GARDE son stepper → le client peut commander
         plusieurs exemplaires du même mélange ; les extras suivent (reconcilier).
         Dawn : le stepper est le custom element <quantity-input>. */
      if (role === 'supplement' || role === 'bonbon') {
        ligne.querySelectorAll('quantity-input').forEach((q) => {
          q.style.display = 'none';
        });
      }
      /* Poubelle : masquée pour le Supplément et les bonbons (le mix reste
         supprimable — supprimer le contenant retire tout le mélange).
         Dawn : la poubelle est le custom element <cart-remove-button>. */
      if (role === 'supplement' || role === 'bonbon') {
        ligne.querySelectorAll('cart-remove-button').forEach((b) => {
          b.style.display = 'none';
        });
      }
      /* Marquage pour le style « bonbons = enfants du contenant » (CSS dans
         nj-melanges-cart.css). Le grammage n'est PAS affiché ; on nettoie un
         éventuel label « <n> g » laissé par une version antérieure. */
      if (role === 'bonbon') {
        ligne.classList.add('nj-bonbon-row');
        ligne.querySelectorAll('.nj-bonbon-poids').forEach((e) => e.remove());
      } else if (role === 'supplement') {
        /* Niché sous le contenant comme un bonbon, mais son prix reste visible. */
        ligne.classList.add('nj-supplement-row');
      } else if (role === 'mix') {
        ligne.classList.add('nj-mix-parent');
      }
    });
  }

  /* ─────────── 4. Réordonnancement : bonbons après leur contenant ─────────── */
  /* Regroupe chaque ligne bonbon immédiatement après la ligne contenant de
     même _mix_id. Ne déplace un <tr> QUE s'il est mal placé → aucune mutation
     quand l'ordre est déjà bon, donc pas de boucle avec le MutationObserver. */
  function reordonnerBonbons() {
    const contenants = []; /* { row, mixId } dans l'ordre du DOM */
    const bonbonsParMix = Object.create(null); /* mixId → [rows] dans l'ordre du DOM */
    const supplementParMix = Object.create(null); /* mixId → [rows] : une ligne extras PAR PALIER (0,99 $, 1,99 $…) */
    document.querySelectorAll('input[data-quantity-line-key]').forEach((input) => {
      const key = input.getAttribute('data-quantity-line-key');
      const role = rolesParKey[key];
      const mixId = mixIdParKey[key];
      if (!mixId) return;
      const row = input.closest('tr.cart-item, .cart-item, tr, li');
      if (!row) return;
      if (role === 'mix') contenants.push({ row, mixId });
      else if (role === 'bonbon') (bonbonsParMix[mixId] || (bonbonsParMix[mixId] = [])).push(row);
      else if (role === 'supplement') (supplementParMix[mixId] || (supplementParMix[mixId] = [])).push(row);
    });
    contenants.forEach(({ row: contRow, mixId }) => {
      /* Enfants du contenant, dans l'ordre : bonbons puis Supplément. On ne
         déplace un <tr> que s'il est mal placé → idempotent (aucune mutation
         quand l'ordre est déjà bon, donc pas de boucle avec le MutationObserver). */
      const enfants = (bonbonsParMix[mixId] || []).slice();
      if (supplementParMix[mixId]) enfants.push(...supplementParMix[mixId]);
      if (!enfants.length) return;
      let ancre = contRow;
      enfants.forEach((row) => {
        if (row.parentNode !== ancre.parentNode) return; /* conteneur différent : on ne touche pas */
        if (ancre.nextElementSibling !== row) {
          ancre.parentNode.insertBefore(row, ancre.nextElementSibling);
        }
        ancre = row;
      });
    });
  }

  /* Verrou visuel + réordonnancement, tous deux idempotents. */
  function appliquer() {
    verrouillerAffichage();
    reordonnerBonbons();
  }

  /* ─────────── Anti-swap au checkout ─────────── */
  /* Le bouton checkout de Dawn (name="checkout", form="cart") soumet le
     formulaire panier, dont les inputs `updates[]` sont POSITIONNELS : Shopify
     applique la Nᵉ valeur à la Nᵉ ligne du panier. Or on réordonne les <tr>
     (supplément/bonbons nichés sous le contenant), donc l'ordre DOM des
     `updates[]` ne correspond plus à l'ordre des lignes serveur → les
     quantités s'appliquent aux mauvaises lignes (contenant facturé ×N, ex.
     345,51 $ au lieu de 60,94 $). Le panier serveur est déjà juste (Dawn
     synchronise chaque changement de quantité via /cart/change.js), donc on
     court-circuite ce POST : dès qu'un mélange est présent, on redirige
     directement vers /checkout au lieu de soumettre le formulaire. */
  function panierContientMix() {
    return (
      !!document.querySelector('input[data-quantity-variant-id="' + CONFIG.EXTRA_VARIANT_ID + '"]') ||
      !!document.querySelector('.nj-mix-parent')
    );
  }
  let checkoutEnCours = false;
  async function resyncPuisCheckout() {
    if (checkoutEnCours) return; /* évite un double déclenchement click + submit */
    checkoutEnCours = true;
    /* Capte une saisie clavier non encore validée (force aussi le 'change' de Dawn). */
    const actif = document.activeElement;
    if (actif && typeof actif.blur === 'function') actif.blur();
    /* Resync KEYÉE par clé de ligne (ordre-indépendant → aucun risque de swap,
       contrairement aux updates[] positionnels du formulaire Dawn) : le serveur
       reflète exactement les inputs visibles, quelle que soit la synchro de Dawn. */
    const updates = {};
    document.querySelectorAll('input[data-quantity-line-key]').forEach((input) => {
      const key = input.getAttribute('data-quantity-line-key');
      const val = parseInt(input.value, 10);
      if (key && Number.isFinite(val)) updates[key] = val;
    });
    try {
      if (Object.keys(updates).length) {
        const req = fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
        /* Plafond de sécurité : ne jamais bloquer le checkout plus de ~2 s. */
        await Promise.race([req, new Promise((r) => setTimeout(r, 2000))]);
      }
    } catch (e) { /* on redirige quand même : le panier serveur reste la source de vérité */ }
    window.location.assign('/checkout');
  }
  function courtCircuiterCheckout(e) {
    const cible = e.target;
    const bouton = cible && cible.closest && cible.closest('[name="checkout"], #checkout');
    if (!bouton) return;
    if (!panierContientMix()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    resyncPuisCheckout();
  }
  document.addEventListener('click', courtCircuiterCheckout, true);
  document.addEventListener('submit', (e) => {
    const s = e.submitter;
    if (s && (s.name === 'checkout' || s.id === 'checkout') && panierContientMix()) {
      e.preventDefault();
      resyncPuisCheckout();
    }
  }, true);

  /* ─────────── Déclencheurs ─────────── */
  /* Intercepte les appels panier du thème pour re-réconcilier après coup */
  const fetchOrigine = window.fetch;
  window.fetch = function (...args) {
    const url = String(args[0] && args[0].url ? args[0].url : args[0] || '');
    const p = fetchOrigine.apply(this, args);
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      p.then(() => setTimeout(reconcilier, 400)).catch(() => {});
    }
    return p;
  };

  /* Drawer/panier ré-rendu dynamiquement → re-verrouiller + réordonner.
     appliquer() ne déclenche des mutations que s'il y a réellement quelque
     chose à corriger (insertion « g » et déplacement de <tr> gardés
     idempotents) → pas de boucle avec l'observer. */
  const obs = new MutationObserver(() => appliquer());
  obs.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('DOMContentLoaded', reconcilier);
  reconcilier();
})();
